/* eslint-disable react/jsx-no-target-blank */
/* eslint-disable @next/next/no-img-element */
import React, {
  useState, useMemo, useRef,
} from 'react';
import Button from '$/components/base/Button';
import IconFont from '$/components/IconFont';
import { Popover } from 'antd';
import { NoSSR } from '@meitu/catalyze-next';
import { useClickAway } from 'ahooks';
import ProductOperation from '@/components/ProductOperation';
import type { OverseaFeaturesSubItem, OverseaFeatureItem } from '@/apiSdk/api.type';
import classNames from 'classnames';
import useIsMobile from '@/hooks/useIsMobile';
import { useRouter } from 'next/router';
import logo from './logo.png';
import styles from './index.module.scss';

function mtStatTrack({
  level_2_id, level_2_name, level_3_id, level_3_name,
}: MtStatTrackProps) {
  window.mtstat?.track('roboneo_official_page_guide_click', {
    level_1_id: 0,
    level_1_name: 'product',
    level_2_id,
    level_2_name,
    level_3_id,
    level_3_name,
  });
}

interface UserInfo {
  uid: string;
  avatar: string;
  screenName: string;
  token: string;
}

export type OverseaOfficialHeaderAppearance = 'transparent' | 'masked';

interface ServerHeaderProps {
  scrolled?: boolean;
  showAlert?: boolean;
  appearance?: OverseaOfficialHeaderAppearance;
  downloadContent?: React.JSX.Element;
  menus: Record<'product' | 'blog', OverseaFeatureItem[]>;
  onLoginClick?: () => void;
  isLoggedIn?: boolean;
  userInfo?: UserInfo;
}

/**
 * 服务端渲染的 Header 组件
 * 提供静态 HTML 结构，客户端水合后由 ClientHeader 接管交互
 */
export default function ServerHeader({
  showAlert,
  scrolled,
  appearance = 'transparent',
  downloadContent,
  menus,
  onLoginClick,
  isLoggedIn,
  userInfo,
}: ServerHeaderProps) {
  // eslint-disable-next-line ban/ban
  const router = useRouter();
  const isMobile = useIsMobile();
  const [showProductMenu, setShowProductMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const productMenuRef = useRef<HTMLDivElement>(null);
  const blogMenuRef = useRef<HTMLDivElement>(null);
  const [showBlogMenu, setShowBlogMenu] = useState(false);
  useClickAway(() => {
    setShowProductMenu(false);
  }, productMenuRef);
  useClickAway(() => {
    setShowBlogMenu(false);
  }, blogMenuRef);

  const isForcedMaskedHeader = appearance === 'masked';
  const isMaskedHeader = isForcedMaskedHeader || scrolled;
  const headerStyle = useMemo(() => {
    const top = isMobile ? '54px' : '44px';
    const background = isForcedMaskedHeader
      ? '#0a0a0a'
      : 'var(--background-backgroundMaskSecondary, rgba(0, 0, 0, 0.50))';
    return {
      background: `${isMaskedHeader ? background : 'transparent'}`,
      backdropFilter: `${isMaskedHeader ? 'blur(25px)' : 'none'}`,
      top: `${showAlert ? top : '0'}`,
    };
  }, [isForcedMaskedHeader, isMaskedHeader, showAlert, isMobile, showProductMenu]);

  const onSubMenuClick = () => {
    setShowProductMenu(false);
    setShowBlogMenu(false);
  };

  const onMouseEnterLoginButton = () => {
    console.log('onMouseEnterLoginButton');
    router.prefetch('/home').then(() => {
      console.log('prefetch home page success');
    }).catch((error) => {
      console.error('prefetch home page error', error);
    });
  };

  return (
    <header
      className={`${styles.header} ${isMaskedHeader ? styles.scrolled : ''}`}
      style={headerStyle}
    >
      <div className={styles.gradientBlur} style={{ visibility: isMaskedHeader ? 'hidden' : 'visible' }}>
        <div />
        <div />
        <div />
        <div />
        <div />
        <div />
      </div>
      <div className={styles.headerContainer}>
        {/* Logo 区域 */}
        <div className={styles.logoSection}>
          <a href="/" className={styles.logoLink}>
            <img src={logo.src} alt="RoboNeo Logo" className={styles.logoImage} />
          </a>
        </div>

        {/* 导航区域 - 服务端渲染静态结构 */}

        <nav className={styles.navSection}>
          {menus.product.length > 0 && (
          <div
            className={styles.navItem}
            ref={productMenuRef}
            onMouseEnter={() => setShowProductMenu(true)}
            onMouseLeave={() => setShowProductMenu(false)}
            onClick={() => {
              window.mtstat?.track('roboneo_official_page_icon_click', {
                icon_id: 'tab_product',
              });
            }}
          >
            Product
            <ProductMenuBox showProductMenu={showProductMenu} menus={menus.product} onSubMenuClick={onSubMenuClick} />
          </div>
          )}
          <a
            href="#price"
            className={classNames(styles.navItem, styles.pricingNavItem)}
            onClick={() => {
              window.mtstat?.track('roboneo_official_page_icon_click', {
                icon_id: 'tab_pricing',
              });
            }}
          >
            Pricing
          </a>
          {menus.blog.length > 0
          && (
          <div
            className={styles.navItem}
            ref={blogMenuRef}
            onMouseEnter={() => setShowBlogMenu(true)}
            onMouseLeave={() => setShowBlogMenu(false)}
            onClick={() => {
              window.mtstat?.track('roboneo_official_page_icon_click', {
                icon_id: 'tab_blog',
              });
            }}
          >
            Blog
            <ProductMenuBox showProductMenu={showBlogMenu} menus={menus.blog} onSubMenuClick={onSubMenuClick} />
          </div>
          )}
        </nav>

        <div className={styles.rightSection}>
          <div className={styles.icon_menu}>
            {/* product operation */}
            <div className={styles.icon_menu_item}>
              <NoSSR>
                <ProductOperation />
              </NoSSR>
            </div>

            <div className={styles.line} />

            {/* discord */}
            <div className={styles.icon_menu_item}>
              <a href="https://discord.gg/JXt2gwDpVp" target="_blank" rel="noreferrer">
                <IconFont name="discord" fontSize={16} />
              </a>
            </div>

            <div className={styles.line} />

            {/* mobile phone */}
            <Popover
              placement="bottom"
              trigger={['hover']}
              overlayClassName="resetPopoverAndArrow"
              content={downloadContent}
            >
              <div className={styles.icon_menu_item}>
                <IconFont name="mobilePhoneV2Bold" fontSize={16} />
              </div>
            </Popover>

          </div>
          {isLoggedIn && userInfo ? (
            <div className={styles.userAvatar}>
              {userInfo.avatar ? (
                <img src={userInfo.avatar} alt={userInfo.screenName} className={styles.avatarImage} />
              ) : (
                <IconFont name="userV3Bold" fontSize={16} />
              )}
            </div>
          ) : (
            <Button
              className={styles.loginButton}
              type="plain"
              leftIcon={<IconFont name="userV3Bold" fontSize={16} />}
              onClick={onLoginClick}
              onMouseEnter={onMouseEnterLoginButton}
            >
              Login
            </Button>
          )}
        </div>

        {/* 移动端菜单按钮 */}
        <div className={styles.mobileMenuButton} onClick={() => setShowMobileMenu(!showMobileMenu)}>
          <IconFont name={showMobileMenu ? 'crossBold' : 'menuListV2Bold'} fontSize={16} color="var(--content-buttonSecondary)" />
        </div>
        <MobileNavSection showMobileMenu={showMobileMenu} menus={menus} onMaskClick={() => setShowMobileMenu(false)} />
      </div>

    </header>
  );
}

interface MtStatTrackProps {
  level_2_id: string;
  level_2_name: string;
  level_3_id: string;
  level_3_name: string;
}

function ProductMenuBox({ showProductMenu, menus, onSubMenuClick }: { showProductMenu: boolean; menus: OverseaFeatureItem[]; onSubMenuClick: () => void; }) {
  const [showInnerListIndex, setShowInnerListIndex] = useState(0);
  const height = useMemo(() => {
    const length = Math.max(menus.length, menus[showInnerListIndex]?.list?.length ?? 0);
    return length * 36 + 4 * (length - 1) + 40;
  }, [menus, showInnerListIndex]);

  const subMenus = useMemo(() => {
    return menus[showInnerListIndex]?.list ?? [];
  }, [menus, showInnerListIndex]);

  const openSubMenu = (subItem: OverseaFeaturesSubItem) => {
    mtStatTrack({
      level_2_id: `${menus[showInnerListIndex]?.category_id}`,
      level_2_name: menus[showInnerListIndex]?.category,
      level_3_id: `${subItem.id}`,
      level_3_name: subItem.title,
    });
    onSubMenuClick();
  };

  return (
    <div className={styles.outerProductMenuBox} style={{ display: showProductMenu ? 'block' : 'none' }}>
      <div
        className={styles.productMenuBox}
        style={{ height }}
      >
        <ul className={styles.productMenuList}>
          {menus.map((item: OverseaFeatureItem, index: number) => (
            <li className={styles.productMenuItem} key={item.category_id} onMouseEnter={() => setShowInnerListIndex(index)}>
              <span className={styles.productMenuItemLink}>{item.category}</span>
              <IconFont
                name="chevronRightFill"
                fontSize={10}
                color="var(--content-secondary)"
                className={styles.chevronRightIcon}
              />
            </li>
          ))}

        </ul>
        <ul className={styles.productSubMenuList}>
          {subMenus.map((subItem: OverseaFeaturesSubItem) => (
            <li className={styles.productSubMenuItem} key={subItem.id}>
              <a
                className={styles.productMenuItemLink}
                href={subItem.link}
                target="_blank"
                onClick={() => openSubMenu(subItem)}
              >
                {subItem.title}
              </a>
            </li>
          ))}
        </ul>

      </div>
    </div>
  );
}

// 移动端导航组件
function MobileNavSection({ showMobileMenu, menus, onMaskClick }: { showMobileMenu: boolean; menus: Record<'product' | 'blog', OverseaFeatureItem[]>; onMaskClick: () => void; }) {
  const [showProductMenu, setShowProductMenu] = useState(false);
  const [showBlogMenu, setShowBlogMenu] = useState(false);
  return (
    <>
      <div className={styles.mobileNavMask} style={{ display: showMobileMenu ? 'block' : 'none' }} onClick={onMaskClick} />
      <nav className={classNames(styles.mobileNavSection, { [styles.show]: showMobileMenu })}>
        <div
          className={classNames(styles.navItem, { [styles.active]: showProductMenu })}
          onClick={() => {
            window.mtstat?.track('roboneo_official_page_icon_click', {
              icon_id: 'tab_product',
            });
            setShowBlogMenu(false);
            setShowProductMenu(!showProductMenu);
          }}
        >
          Product
          <IconFont name="chevronRightFill" fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />
        </div>
        {showProductMenu && (
        <MobileProductMenu showProductMenu={showProductMenu} menus={menus.product} />
        )}
        <a
          href="#price"
          className={styles.navItem}
          onClick={() => {
            window.mtstat?.track('roboneo_official_page_icon_click', {
              icon_id: 'tab_pricing',
            });
            onMaskClick();
          }}
        >
          Pricing
          <IconFont name="chevronRightFill" fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />
        </a>
        {menus.blog.length > 0 && (
          <>
            <div
              className={classNames(styles.navItem, { [styles.active]: showBlogMenu })}
              onClick={() => {
                window.mtstat?.track('roboneo_official_page_icon_click', {
                  icon_id: 'tab_blog',
                });
                setShowProductMenu(false);
                setShowBlogMenu(!showBlogMenu);
              }}
            >
              Blog
              <IconFont name="chevronRightFill" fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />
            </div>
            {showBlogMenu && (
              <MobileProductMenu showProductMenu={showBlogMenu} menus={menus.blog} />
            )}
          </>
        )}
      </nav>
    </>
  );
}

interface MobileProductMenuProps {
  showProductMenu: boolean;
  menus: OverseaFeatureItem[] | OverseaFeaturesSubItem[];
  indent?: number;
}

// 移动端产品菜单
function MobileProductMenu({ showProductMenu, menus, indent = 0 }: MobileProductMenuProps) {
  const [showMenuIndex, setShowMenuIndex] = useState(-1);
  return (
    <ul
      className={styles.mobileProductMenuList}
      style={{
        display: showProductMenu ? 'block' : 'none',
        paddingLeft: indent * 24 + (indent === 0 ? 36 : 0),
      }}
    >
      {menus.map((item, index) => {
        const isLeaf = ((item as OverseaFeatureItem).list?.length ?? 0) === 0;
        const key = (item as OverseaFeatureItem).category_id ?? (item as OverseaFeaturesSubItem).id;
        const isActive = showMenuIndex === index;
        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
          <li
            className={styles.productMenuItem}
            key={key}
            onClick={(e) => {
              e.stopPropagation();
              if (isLeaf) {
                mtStatTrack({
                  level_2_id: `${(item as OverseaFeatureItem).category_id}`,
                  level_2_name: (item as OverseaFeatureItem).category,
                  level_3_id: `${(item as OverseaFeaturesSubItem).id}`,
                  level_3_name: (item as OverseaFeaturesSubItem).title,
                });
              } else {
                setShowMenuIndex(isActive ? -1 : index);
              }
            }}
          >
            <div className={styles.inner_item_box}>
              {!isLeaf
                ? <span className={classNames(styles.productMenuItemLink, { [styles.hightlight]: isActive })}>{(item as OverseaFeatureItem).category || (item as OverseaFeaturesSubItem).title}</span>
                : <a className={styles.productMenuItemLink} href={(item as OverseaFeaturesSubItem).link}>{(item as OverseaFeatureItem).category || (item as OverseaFeaturesSubItem).title}</a>}
              {!isLeaf && <IconFont name={isActive ? 'chevronDownFill' : 'chevronRightFill'} fontSize={12} color="var(--content-secondary)" className={styles.chevronRightIcon} />}
            </div>
            {!isLeaf && (
            <MobileProductMenu
              showProductMenu={isActive}
              menus={(item as OverseaFeatureItem).list || []}
              indent={indent + 1}
            />
            )}
          </li>
        );
      })}
    </ul>
  );
}
